import type { IntlShape } from 'react-intl';

import { useAppSelector, useFeatureFlagSplit } from '@verantos-dev/app-state-base';
import { useCohortEditable } from '@verantos-dev/app-state-cohort-builder';
import {
  selectIsSaving,
  selectLatestStatus,
  useCohortDefinitionStore,
} from '@verantos-dev/app-state-cohort-definition';
import { selectResearchAssistantIsWaitingForResponse } from '@verantos-dev/app-state-research-assistant';
import { CohortDefinitionStatus } from '@verantos-dev/types-cohorts';

export interface CohortBuilderEditState {
  /** True when user cannot edit at all (not owner / not editable) */
  viewMode: boolean;
  /** True when cohort status is PROCESSING or FINAL */
  isProcessing: boolean;
  /** True when Research Assistant is generating a response */
  isWaitingForRA: boolean;
}

export interface ActionsDisabledInfo {
  actionsDisabled: boolean;
  actionTooltip: string | undefined;
}

export function getActionsDisabledInfo(
  intl: IntlShape,
  isProcessing: boolean,
  isWaitingForRA: boolean,
  isSaving = false
): ActionsDisabledInfo {
  const actionsDisabled = isProcessing || isWaitingForRA || isSaving;
  const actionTooltip = isProcessing
    ? intl.formatMessage({
        id: 'projects.cohortBuilder.cohortBuilderItemViewCard.actionTooltip.processing',
      })
    : isWaitingForRA
    ? intl.formatMessage({
        id: 'projects.cohortBuilder.cohortBuilderItemViewCard.actionTooltip.raProcessing',
      })
    : isSaving
    ? intl.formatMessage({
        id: 'projects.cohortBuilder.cohortBuilderItemViewCard.actionTooltip.saving',
      })
    : undefined;
  return { actionsDisabled, actionTooltip };
}

export const useCohortBuilderViewMode = (): CohortBuilderEditState => {
  const { isEditable } = useCohortEditable();
  const effectiveStatus = useCohortDefinitionStore(selectLatestStatus);

  const isProcessing =
    !!effectiveStatus &&
    [CohortDefinitionStatus.PROCESSING, CohortDefinitionStatus.FINAL].includes(effectiveStatus);
  const isWaitingForRA = !!useAppSelector(selectResearchAssistantIsWaitingForResponse);
  const viewMode = !isEditable || isProcessing || isWaitingForRA;
  return { viewMode, isProcessing, isWaitingForRA };
};

/**
 * Returns the effective `isSaving` for gating in-place edit actions (delete,
 * inline edit). On `explicit_save`, edits are optimistic and the round-trip
 * tracker is reserved for RA send + Save/Revert/Process buttons — so individual
 * UI controls must not block on it.
 *
 * TODO: cleanup with explicit_save ff
 */
export const useIsSavingForUiActions = (): boolean => {
  const isExplicitSave = useFeatureFlagSplit('explicit_save');
  const isSaving = useCohortDefinitionStore(selectIsSaving);
  return isExplicitSave ? false : isSaving;
};
